import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { X, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { getInitials } from 'utils/getInitials';
import { formatDate, parseDateKey, toTitleCase } from 'utils/helpers';
import { showToast } from 'utils/toast';
import { QUERY_KEYS } from 'service/querykeys';
import {
  useCancellationRequestQuery,
  useApproveCancellationRequestMutation,
  useRejectCancellationRequestMutation,
} from 'service/hrms/hooks/leaveRequestHooks';
import {
  LeaveCancellationStatus,
  getCancellationDecisionSourceLabel,
  getLeaveErrorMessage,
} from 'types/leaveRequest';
import RequiredMark from 'components/common/RequiredMark';

// Review of an employee's request to cancel an already-approved leave,
// opened from a LEAVE_CANCELLATION_REQUEST notification (referenceUuid is the
// cancellation request's uuid — these notifications carry no deepLink). Same
// look as the regularization review modal; decisions follow regular leave
// approval: buttons only from canApprove/canReject, sent with the version
// from the GET.

interface LeaveCancellationReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  cancellationRequestUuid?: string | null;
}

const REASON_MAX_LENGTH = 1000;

// A decision that lost a race (someone else decided first, or the record
// changed since it loaded) — reload and show whatever state actually won.
const CONFLICT_CODES = ['STALE_RECORD_VERSION', 'REQUEST_ALREADY_ACTIONED'];

const STATUS_BANNER: Record<LeaveCancellationStatus, { text: string; className: string }> = {
  PENDING: {
    text: 'Awaiting your decision on this cancellation request',
    className: 'bg-[#261E14] border-[#523A1E] text-[#EAA846]',
  },
  APPROVED: {
    text: 'Cancellation accepted — the leave has been cancelled',
    className: 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400',
  },
  REJECTED: {
    text: 'Cancellation rejected — the leave stays approved',
    className: 'bg-red-950/40 border-red-500/30 text-red-400',
  },
  EXPIRED: {
    text: 'Expired — the leave started before a decision was made',
    className: 'bg-slate-800/40 border-slate-700 text-slate-300',
  },
  NO_LONGER_REQUIRED: {
    text: 'No longer required — a holiday change removed the remaining leave days',
    className: 'bg-slate-800/40 border-slate-700 text-slate-300',
  },
};

const formatLeaveDate = (dateKey: string) => formatDate(parseDateKey(dateKey));

const LeaveCancellationReviewModal: React.FC<LeaveCancellationReviewModalProps> = ({
  isOpen,
  onClose,
  cancellationRequestUuid,
}) => {
  const queryClient = useQueryClient();
  // Callers clear the uuid as they close the modal — keep showing the last
  // request through the closing animation instead of flashing the error state.
  const lastUuidRef = useRef<string | null>(null);
  if (cancellationRequestUuid) lastUuidRef.current = cancellationRequestUuid;
  const requestUuid = cancellationRequestUuid || lastUuidRef.current;
  const {
    data: request,
    isLoading,
    isError,
    refetch,
  } = useCancellationRequestQuery(requestUuid || undefined);
  const approveMutation = useApproveCancellationRequestMutation();
  const rejectMutation = useRejectCancellationRequestMutation();

  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  // 403 — the viewer can't act on this request.
  const [actionError, setActionError] = useState<string | null>(null);
  // 409 — shown above the reloaded, latest state.
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  // Which button is in flight, so only the clicked one shows a spinner.
  const [pendingAction, setPendingAction] = useState<'approve' | 'reject' | null>(null);

  // Each opening starts clean — a previous request's draft reason or error
  // must never carry over to the next one — and re-reads the request, since
  // a cached copy from an earlier opening may already have been decided.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setReasonError(null);
      setActionError(null);
      setConflictNotice(null);
      setPendingAction(null);
      if (requestUuid) refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, requestUuid]);

  const canApprove = !!request?.canApprove;
  const canReject = !!request?.canReject;
  const canDecide = canApprove || canReject;
  const isDecided = !!request && request.status !== 'PENDING';

  const handleDecision = (action: 'approve' | 'reject') => {
    if (!request) return;
    const trimmedReason = reason.trim();
    if (request.decisionReasonRequired && !trimmedReason) {
      setReasonError('A reason is required for this decision.');
      return;
    }
    setReasonError(null);
    setActionError(null);
    setConflictNotice(null);
    setPendingAction(action);

    const mutation = action === 'approve' ? approveMutation : rejectMutation;
    mutation.mutate(
      { uuid: request.uuid, version: request.version, reason: trimmedReason || null },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HRM_NOTIFICATIONS });
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HRM_NOTIFICATIONS_UNREAD_COUNT });
          showToast.success(
            action === 'approve' ? 'Cancellation accepted.' : 'Cancellation rejected.',
          );
          onClose();
        },
        onError: (error: any) => {
          // The shared axios interceptor puts the HTTP status on `status` and
          // the backend's code on `errorCode` (see normalizeApiError).
          const message = getLeaveErrorMessage(error, 'Failed to update this cancellation request.');
          if (error?.status === 409 || CONFLICT_CODES.includes(error?.errorCode)) {
            setReason('');
            setConflictNotice(
              'This request was updated by someone else. Showing the latest status.',
            );
            refetch();
          } else if (error?.status === 422) {
            setReasonError(message);
          } else if (error?.status === 403) {
            setActionError(message);
          } else {
            showToast.error(message);
          }
        },
        onSettled: () => setPendingAction(null),
      },
    );
  };

  const banner = request
    ? request.status === 'PENDING' && !canDecide
      ? {
          text: 'Pending review — you can view this request but not decide it',
          className: 'bg-slate-800/40 border-slate-700 text-slate-300',
        }
      : STATUS_BANNER[request.status] ?? {
          text: request.status,
          className: 'bg-slate-800/40 border-slate-700 text-slate-300',
        }
    : null;

  const decisionSourceLabel = request
    ? getCancellationDecisionSourceLabel(request.decisionSource)
    : null;
  const isBusy = pendingAction !== null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75 backdrop-blur-xs" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[480px] transform overflow-hidden rounded-2xl bg-[#141824] border border-[#222838] p-6 text-left align-middle shadow-2xl transition-all space-y-5">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-3">
                    <Loader2 className="w-8 h-8 text-[#8B5CF6] animate-spin" />
                    <p className="text-slate-400 text-sm">Loading cancellation request...</p>
                  </div>
                ) : isError || !request ? (
                  <div className="py-6 text-center space-y-3">
                    <p className="text-red-400 text-sm">
                      Failed to load cancellation request details.
                    </p>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-xs font-semibold text-slate-300 bg-[#222836] rounded-lg"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-full bg-[#8B5CF6] flex items-center justify-center text-white font-semibold text-base shrink-0 shadow-sm">
                          {getInitials(request.employeeName)}
                        </div>
                        <div>
                          <h3 className="text-white font-bold text-lg sm:text-[19px] leading-tight">
                            {request.employeeName}
                          </h3>
                          <p className="text-[#8E99A8] text-xs sm:text-sm font-normal mt-0.5">
                            Leave cancellation request
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={onClose}
                        type="button"
                        className="w-8 h-8 rounded-lg bg-[#222836] hover:bg-[#2C3446] text-[#8E99A8] hover:text-white flex items-center justify-center transition-colors shrink-0"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {conflictNotice && (
                      <div className="w-full rounded-xl px-4 py-3 border bg-[#3B82F6]/10 border-[#3B82F6]/30 text-[#93C5FD]">
                        <p className="text-xs sm:text-sm leading-snug">{conflictNotice}</p>
                      </div>
                    )}

                    {banner && (
                      <div className={`w-full rounded-xl px-4 py-3 text-center sm:text-left border ${banner.className}`}>
                        <p className="font-medium text-xs sm:text-sm leading-snug">{banner.text}</p>
                      </div>
                    )}

                    {/* Leave being cancelled */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Leave type
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl px-4 py-3 text-white font-medium text-sm sm:text-base">
                          {toTitleCase(request.leaveType)}
                        </div>
                      </div>
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Days
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl px-4 py-3 text-white font-medium text-sm sm:text-base">
                          {request.chargeableDays} day{request.chargeableDays !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                        Dates
                      </label>
                      <div className="w-full bg-[#201C2E] border border-[#3B3252] rounded-xl px-4 py-3 text-[#A78BFA] font-semibold text-sm sm:text-base">
                        {request.fromDate === request.toDate
                          ? formatLeaveDate(request.fromDate)
                          : `${formatLeaveDate(request.fromDate)} – ${formatLeaveDate(request.toDate)}`}
                      </div>
                    </div>

                    {/* Cancellation reason */}
                    <div>
                      <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                        Cancellation reason from {request.employeeName?.split(' ')[0] || 'Employee'}
                      </label>
                      <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl p-4 text-[#D1D5DB] text-xs sm:text-sm leading-relaxed font-normal min-h-[72px] break-words">
                        {request.reason || 'No reason provided'}
                      </div>
                    </div>

                    {/* The decision, once made */}
                    {isDecided && (request.decidedByName || request.decisionReason) && (
                      <div>
                        <label className="text-[#8E99A8] text-xs font-medium mb-1.5 block">
                          Decision
                          {request.decidedByName ? ` by ${request.decidedByName}` : ''}
                          {decisionSourceLabel ? ` (${decisionSourceLabel})` : ''}
                        </label>
                        <div className="w-full bg-[#1A1F2C] border border-[#272E3F] rounded-xl p-3.5 text-[#D1D5DB] text-xs sm:text-sm leading-relaxed break-words">
                          {request.decisionReason || 'No reason provided.'}
                        </div>
                      </div>
                    )}

                    {/* Reviewer's reason */}
                    {canDecide && (
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label
                            htmlFor="cancellation-decision-reason"
                            className="text-[#8E99A8] text-xs font-medium block"
                          >
                            Reason{' '}
                            {request.decisionReasonRequired ? (
                              <RequiredMark />
                            ) : (
                              <span className="text-slate-500">(Optional)</span>
                            )}
                          </label>
                          <span className="text-[11px] text-slate-500">
                            {reason.length}/{REASON_MAX_LENGTH}
                          </span>
                        </div>
                        <textarea
                          id="cancellation-decision-reason"
                          rows={2}
                          maxLength={REASON_MAX_LENGTH}
                          value={reason}
                          onChange={(e) => {
                            setReason(e.target.value);
                            if (reasonError) setReasonError(null);
                          }}
                          placeholder={
                            request.decisionReasonRequired
                              ? 'A reason is required for this decision...'
                              : 'Add a note for the employee...'
                          }
                          className={`w-full bg-[#1A1F2C] border ${
                            reasonError ? 'border-red-500' : 'border-[#272E3F] focus:border-[#8B5CF6]'
                          } rounded-xl px-3.5 py-2.5 text-white text-xs sm:text-sm outline-none transition-colors resize-none`}
                        />
                        {reasonError && (
                          <p className="text-xs text-red-400 mt-1">{reasonError}</p>
                        )}
                      </div>
                    )}

                    {actionError && (
                      <div className="w-full rounded-xl px-4 py-3 border bg-red-950/40 border-red-500/30 text-red-400">
                        <p className="text-xs sm:text-sm leading-snug">{actionError}</p>
                      </div>
                    )}

                    {/* Actions */}
                    {canDecide ? (
                      <div className={`grid gap-3.5 pt-1 ${canApprove && canReject ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {canReject && (
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => handleDecision('reject')}
                            className="w-full bg-[#351C24] hover:bg-[#45202D] border border-[#5C2735] text-[#F87171] font-semibold py-3 px-4 rounded-xl transition-all active:scale-[0.98] text-sm sm:text-base flex items-center justify-center cursor-pointer disabled:opacity-50"
                          >
                            {pendingAction === 'reject' ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              'Reject Cancellation'
                            )}
                          </button>
                        )}
                        {canApprove && (
                          <button
                            type="button"
                            disabled={isBusy}
                            onClick={() => handleDecision('approve')}
                            className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-semibold py-3 px-4 rounded-xl transition-all active:scale-[0.98] text-sm sm:text-base flex items-center justify-center cursor-pointer shadow-lg shadow-emerald-950/30 disabled:opacity-50"
                          >
                            {pendingAction === 'approve' ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              'Accept Cancellation'
                            )}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={onClose}
                          className="w-full bg-[#1A1F2C] hover:bg-[#232938] border border-[#272E3F] text-slate-300 font-semibold py-3 px-4 rounded-xl transition-all text-sm sm:text-base flex items-center justify-center"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default LeaveCancellationReviewModal;
